import React, { useEffect } from "react";
import "./App.css";
import "./styles/Blog.css";
import { BRAND_PAGES, brandPath } from "./Brands.js";

/* One template for every brand page. Usage in App.js:
   <Route path="/brands/sub-zero-repair" element={<BrandPage slug="sub-zero" />} />
   (see the instructions for a version that creates all routes at once) */

const SITE = "https://atappliancemaster.com";
const PHONE_DISPLAY = "(331) 600-4935";
const PHONE_LINK = "tel:+13316004935";

export default function BrandPage({ slug }) {
  const brand = BRAND_PAGES.find((b) => b.slug === slug);
  const url = brand ? SITE + brandPath(brand.slug) : SITE;

  useEffect(() => {
    if (!brand) return;
    document.title = brand.titleTag;
    const set = (selector, tag, attr, key, valueAttr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(tag);
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute(valueAttr, value);
    };
    set('meta[name="description"]', "meta", "name", "description", "content", brand.description);
    set('link[rel="canonical"]', "link", "rel", "canonical", "href", url);
  }, [brand, url]);

  if (!brand) {
    return (
      <main className="blog-post">
        <article>
          <h1>Page not found</h1>
          <p><a href="/">Back to the homepage</a></p>
        </article>
      </main>
    );
  }

  const faqs = [
    ...brand.faqs,
    [
      `Are you a factory-authorized ${brand.name} service center?`,
      `No. AT Appliance Repair and Maintenance is an independent repair company. If your ${brand.name} appliance is still under the manufacturer's warranty, contact ${brand.name} first, because work by an independent company may not be covered by that warranty.`,
    ],
    [
      `How much does a ${brand.name} repair cost?`,
      "Every repair starts with a $99 diagnostic and a written quote for parts and labor. If you go ahead, the $99 is waived. Most repairs are covered by a 90-day warranty. When a part has to be ordered, you pay 50% when we order it and 50% once the repair is done.",
    ],
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `${brand.name} appliance repair`,
      serviceType: "Appliance repair",
      url,
      description: brand.description,
      provider: {
        "@type": "HomeAndConstructionBusiness",
        name: "AT Appliance Repair and Maintenance",
        telephone: "+1-331-600-4935",
        url: SITE + "/",
        address: { "@type": "PostalAddress", addressLocality: "Plainfield", addressRegion: "IL", postalCode: "60586", addressCountry: "US" },
      },
      areaServed: ["Plainfield, IL", "Naperville, IL", "Aurora, IL", "Joliet, IL", "Oswego, IL", "Yorkville, IL"],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Our Services", item: SITE + "/services" },
        { "@type": "ListItem", position: 3, name: `${brand.name} Repair`, item: url },
      ],
    },
  ];

  const otherBrands = BRAND_PAGES.filter((b) => b.slug !== brand.slug);

  return (
    <main className="blog-post">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      <nav className="breadcrumb" aria-label="Breadcrumb">
        <a href="/">Home</a> / <a href="/services">Our Services</a> / <span>{brand.name} Repair</span>
      </nav>

      <article>
        <header className="post-header">
          <p className="post-kicker">Luxury appliance repair</p>
          <h1>{brand.h1}</h1>
          <p className="post-lead">{brand.lead}</p>
          <p>
            <a className="schedule-button" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>{" "}
            <a className="schedule-button cta-secondary" href="/contact">Schedule Service</a>
          </p>
          <p className="brand-facts">
            Same-day service in most cases · $99 diagnostic, waived with repair · 90-day warranty on most repairs
          </p>
        </header>

        <h2>{brand.name} appliances we repair</h2>
        <ul className="check-list">
          {brand.appliances.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>

        <h2>Common {brand.name} problems we fix</h2>
        <div className="problem-list">
          {brand.problems.map(([title, text]) => (
            <div className="problem" key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>

        <h2>How a {brand.name} repair works</h2>
        <ol className="steps">
          <li><strong>Diagnostic visit.</strong> A technician inspects your {brand.name} appliance, finds the cause and gives you a written quote. The visit is $99.</li>
          <li><strong>You approve the repair.</strong> The $99 is waived. If a part has to be ordered, you pay 50% so we can order the part for your exact model.</li>
          <li><strong>We complete the repair.</strong> Once the part arrives, we install it and test the appliance with you.</li>
          <li><strong>You pay the balance.</strong> The remaining 50% is due only after the appliance works. Most repairs are covered by a 90-day warranty.</li>
        </ol>
        <p>
          Read more about <a href="/blog">our 50/50 payment plan</a>.
        </p>

        <h2>Frequently asked questions</h2>
        <div className="post-faq">
          {faqs.map(([q, a]) => (
            <div key={q}>
              <h3>{q}</h3>
              <p>{a}</p>
            </div>
          ))}
        </div>

        <aside className="post-cta">
          <h2>{brand.name} repair near you</h2>
          <p>
            We serve <a href="/illinois-service-area/appliance-repair-plainfield-illinois">Plainfield</a>,{" "}
            <a href="/illinois-service-area/appliance-repair-naperville-illinois">Naperville</a>, Aurora, Joliet,
            Oswego, Yorkville, <a href="/illinois-service-area/appliance-repair-bolingbrook-illinois">Bolingbrook</a>{" "}
            and <a href="/serviceareas">nearby towns</a>, 7 days a week from 8 a.m. to 9 p.m.
          </p>
          <p>
            <a className="schedule-button" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>{" "}
            <a className="schedule-button cta-secondary" href="/contact">Schedule Service</a>
          </p>
        </aside>

        <h2 className="other-brands-title">Other luxury brands we repair</h2>
        <ul className="other-brands">
          {otherBrands.map((b) => (
            <li key={b.slug}><a href={brandPath(b.slug)}>{b.name} repair</a></li>
          ))}
        </ul>

        <p className="brand-disclaimer">
          {brand.name} is a trademark of its owner. AT Appliance Repair and Maintenance is an independent repair
          company and is not affiliated with, endorsed by or authorized by {brand.name}.
        </p>
      </article>

      <a className="mobile-call" href={PHONE_LINK}>Call {PHONE_DISPLAY}</a>
    </main>
  );
}